import React from "react";
import { Locale } from "@dealer-center/ddc-core-utils";
import { Alert, Box } from "@dealer-center/core-components";
import { CONTAINER_STYLES } from "../styleConstants";
import { SupportCard } from "./SupportCard";
import { SectionHeader } from "./SectionHeader";
import { getUrlIncluding } from "../utils/utils";

interface SupportContactsSectionProps {
  supportContacts?: any[];
  singleViewOfCustomer?: string | false;
  labelLinks?: string[];
  marginTop?: string;
  marginBottom?: string;
}

export const SupportContactsSection: React.FC<SupportContactsSectionProps> = ({
  supportContacts,
  singleViewOfCustomer,
  labelLinks,
  marginTop = "40px",
  marginBottom = "40px"
}) => {
  const helpCenterUrl =
    getUrlIncluding(labelLinks ?? [], ["HELP_CENTER"]) ??
    getUrlIncluding(labelLinks ?? [], ["Help Center"]);

  const rawAccountManager = supportContacts?.find(c => c.name);

  // Help Center goes on Account Manager card when present, otherwise falls back to first tech contact
  const injectHelpCenter = (contact: any) =>
    contact && helpCenterUrl
      ? {
          ...contact,
          urls: [
            ...(contact.urls ?? []),
            {
              value: helpCenterUrl,
              label: String(Locale.getText("HELP_CENTER"))
            }
          ]
        }
      : contact;

  const accountManagerData = rawAccountManager
    ? injectHelpCenter(rawAccountManager)
    : undefined;

  // All non-account-manager contacts; help center injected into first when no AM exists
  const techContacts = (
    supportContacts?.filter(c => c !== rawAccountManager) ?? []
  ).map((c, i) => (!rawAccountManager && i === 0 ? injectHelpCenter(c) : c));

  return (
    <Box
      className="support-section"
      sx={{
        marginTop,
        marginBottom
      }}
    >
      <SectionHeader title={String(Locale.getText("CC_SUPPORT_CONTACTS"))} />

      {/* Account ID Notice - Only show if singleViewOfCustomer exists */}
      {singleViewOfCustomer && (
        <Box sx={{ maxWidth: 872, marginBottom: "16px" }}>
          <Alert role="alert" type="info">
            {String(Locale.getText("ACCOUNT_ID_NOTICE"))}:{" "}
            <strong>{singleViewOfCustomer}</strong>
          </Alert>
        </Box>
      )}

      <Box
        className="support-contacts-list"
        sx={{
          ...CONTAINER_STYLES.flexWrap,
          maxWidth: 872
        }}
      >
        {/* Your Account Manager Card */}
        {accountManagerData && (
          <SupportCard
            callCoxLabel={
              techContacts.length === 0
                ? String(Locale.getText("CALL_COX"))
                : undefined
            }
            contactData={accountManagerData}
            iconBackgroundColor="#009E77"
            iconKey="user"
            title={
              accountManagerData.title?.toLowerCase() === "contact"
                ? String(Locale.getText("YOUR_ACCOUNT_MANAGER"))
                : (accountManagerData.title ??
                  String(Locale.getText("YOUR_ACCOUNT_MANAGER")))
            }
          />
        )}

        {/* Technical Support Cards - renders all non-account-manager contacts */}
        {techContacts.map((contact, i) => (
          <SupportCard
            callCoxLabel={
              i === techContacts.length - 1
                ? String(Locale.getText("CALL_COX"))
                : undefined
            }
            contactData={contact}
            iconBackgroundColor="#3996D4"
            iconKey="phone"
            key={contact.title ?? i}
            title={
              contact.title === "Technical Support"
                ? String(Locale.getText("TECHNICAL_SUPPORT"))
                : (contact.title ?? String(Locale.getText("TECHNICAL_SUPPORT")))
            }
          />
        ))}
      </Box>
    </Box>
  );
};
